// frontend/src/components/SDR/SessionCard.jsx
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { ChevronDownIcon, ChevronUpIcon, DevicePhoneMobileIcon, GlobeAltIcon } from '@heroicons/react/24/outline';

const Field = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="text-sm">
      <span className="font-semibold text-gray-700">{label}: </span>
      <span className="text-gray-800">{String(value)}</span>
    </div>
  );
};

const qualificationStyle = (level) => {
  const l = String(level || '').toLowerCase();
  if (l === 'alto') return 'bg-green-100 text-green-800';
  if (l === 'medio') return 'bg-yellow-100 text-yellow-800';
  return 'bg-gray-100 text-gray-700';
};

const Transcript = ({ turns }) => {
  const [open, setOpen] = useState(false);
  if (!turns || turns.length === 0) return null;
  return (
    <div className="mt-3 border-t border-gray-200 pt-3">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900">
        {open ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
        Transcrição ({turns.length} {turns.length === 1 ? 'turno' : 'turnos'})
      </button>
      {open && (
        <ul className="mt-2 space-y-2">
          {turns.map((t, i) => (
            <li key={t.turn_number ?? i} className="text-sm">
              <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-semibold mr-2 ${t.speaker === 'ai' ? 'bg-blue-100 text-blue-800' : t.speaker === 'system' ? 'bg-gray-200 text-gray-700' : 'bg-emerald-100 text-emerald-800'}`}>{t.speaker}</span>
              <span className="text-gray-800 whitespace-pre-wrap">{t.message_text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const SessionCard = ({ s }) => {
  const isWhatsapp = String(s.channel || '').toLowerCase() === 'whatsapp' || !!s.whatsapp_number;
  const dt = s.started_at ? new Date(s.started_at).toLocaleString('pt-BR') : '';
  const dur = s.duration_seconds ? `${Math.floor(s.duration_seconds / 60)}m${String(s.duration_seconds % 60).padStart(2, '0')}s` : null;
  const report = s.summary_internal || s.summary_for_client || '';
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-semibold text-gray-900">{s.company_name || 'Empresa não informada'}</div>
          <div className="text-sm text-gray-600">{[s.prospect_name, s.prospect_role].filter(Boolean).join(' · ') || 'Prospect'}</div>
          <div className="text-xs text-gray-500 mt-1">{[dt, dur, s.transcript ? `${s.transcript.length} mensagens` : null].filter(Boolean).join(' · ')}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
            {isWhatsapp ? <DevicePhoneMobileIcon className="h-3.5 w-3.5" /> : <GlobeAltIcon className="h-3.5 w-3.5" />}
            {isWhatsapp ? 'WhatsApp' : 'Web/Voz'}
          </span>
          {s.qualification_level && (
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${qualificationStyle(s.qualification_level)}`}>
              {s.qualification_level}{s.qualification_score != null ? ` ${s.qualification_score}/15` : ''}
            </span>
          )}
        </div>
      </div>
      <div className="space-y-1 mb-3">
        <Field label="Serviço recomendado" value={s.recommended_service} />
        <Field label="Serviço necessário" value={s.service_needed} />
        <Field label="Pain points" value={s.pain_points} />
        <Field label="Situação atual" value={s.current_situation} />
        <Field label="Volume estimado" value={s.volume_estimated} />
        <Field label="Faixa de preço" value={s.estimated_price_range} />
        <Field label="Timeline" value={s.timeline} />
        <Field label="Orçamento" value={s.budget_indication} />
      </div>
      {report && (
        <div className="border-t border-gray-200 pt-3">
          <div className="text-xs font-semibold uppercase text-gray-500 mb-1">Relatório Interno</div>
          <div className="prose prose-sm max-w-none text-gray-800"><ReactMarkdown>{report}</ReactMarkdown></div>
        </div>
      )}
      <Transcript turns={s.transcript} />
    </div>
  );
};

export default SessionCard;
